import React, { useState, useEffect, useMemo } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Check, TrendingUp, TrendingDown, Scale, TestTube, FileCheck, Calculator, Settings2, Info, ArrowRight, Truck, MapPin, Calendar, Printer } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { calculateGrainDiscounts } from "@/utils/grainCalculationEngine";
import { Plus, Trash2, GripVertical, AlertTriangle } from "lucide-react";
import { imprimirTicketGrao } from "./TicketGraoImpressao";
import PartnerSearchPrecision from "../shared/PartnerSearchPrecision";

// --- Configuration & Constants ---

const produtos = [
  { value: "soja", label: "Soja", icon: "🌱", color: "text-green-600" },
  { value: "milho", label: "Milho", icon: "🌽", color: "text-yellow-500" },
  { value: "sorgo", label: "Sorgo", icon: "🌾", color: "text-orange-600" },
  { value: "trigo", label: "Trigo", icon: "🌾", color: "text-amber-600" },
  { value: "feijao", label: "Feijão", icon: "🫘", color: "text-red-700" },
  { value: "arroz", label: "Arroz", icon: "🍚", color: "text-stone-400" },
  { value: "cafe", label: "Café", icon: "☕", color: "text-amber-900" },
  { value: "algodao", label: "Algodão", icon: "🏵️", color: "text-blue-600" },
  { value: "amendoim", label: "Amendoim", icon: "🥜", color: "text-yellow-800" },
  { value: "girassol", label: "Girassol", icon: "🌻", color: "text-yellow-600" },
  { value: "outro", label: "Outro", icon: "📦", color: "text-gray-500" }
];

const classificacoesPadrao = [
  { value: "tipo_1", label: "Tipo 1 (Padrão)" },
  { value: "tipo_2", label: "Tipo 2" },
  { value: "comercial", label: "Comercial" }
];

const buildDefaultDiscountConfig = () => ({
  soja: {
    standardMoisture: 14.0,
    impurityTolerance: 1.0,
    technicalShrinkPct: 0.0,
    factors: [
      { code: 'UMIDADE', tolerance: 14, discount_mode: 'PESO', brackets: [{ from: 14, to: 15, discount_pct: 2 }, { from: 15, to: 16, discount_pct: 3.5 }, { from: 16, to: 17, discount_pct: 5 }] },
      { code: 'AVARIADOS', tolerance: 8, discount_mode: 'PESO', brackets: [{ from: 8, to: 10, discount_pct: 1 }, { from: 10, to: 15, discount_pct: 2 }] },
      { code: 'ARDIDOS', tolerance: 4, discount_mode: 'PESO', brackets: [{ from: 4, to: 6, discount_pct: 1 }, { from: 6, to: 10, discount_pct: 2 }] }
    ]
  },
  milho: {
    standardMoisture: 14.0,
    impurityTolerance: 1.0,
    technicalShrinkPct: 0.0,
    factors: [
      {
        code: 'UMIDADE',
        tolerance: 14,
        discount_mode: 'PESO',
        brackets: [
          { from: 14, to: 15, discount_pct: 2.00 },
          { from: 15, to: 16, discount_pct: 3.50 },
          { from: 16, to: 17, discount_pct: 5.00 },
          { from: 17, to: 18, discount_pct: 6.50 },
          { from: 18, to: 19, discount_pct: 7.00 },
          { from: 19, to: 20, discount_pct: 8.00 },
          { from: 20, to: 21.5, discount_pct: 10.25 },
          { from: 21.5, to: 24, discount_pct: 14.00 }
        ]
      },
      { code: 'AVARIADOS', tolerance: 6, discount_mode: 'PESO', brackets: [{ from: 6, to: 8, discount_pct: 1 }, { from: 8, to: 12, discount_pct: 2 }] },
      { code: 'ARDIDOS', tolerance: 4, discount_mode: 'PESO', brackets: [{ from: 4, to: 6, discount_pct: 1 }, { from: 6, to: 10, discount_pct: 2 }] }
    ]
  },
  outro: {
    standardMoisture: 14.0,
    impurityTolerance: 1.0,
    technicalShrinkPct: 0.0,
    factors: []
  }
});

const mergeDiscountConfig = (base, saved) => {
  const merged = { ...base };
  Object.keys(saved || {}).forEach((key) => {
    if (!base[key]) return;
    merged[key] = {
      standardMoisture: saved[key]?.standardMoisture ?? base[key]?.standardMoisture,
      impurityTolerance: saved[key]?.impurityTolerance ?? base[key]?.impurityTolerance,
      technicalShrinkPct: 0.0,
      factors: Array.isArray(saved[key]?.factors) ? saved[key].factors : (base[key]?.factors || [])
    };
  });
  return merged;
};

// --- Custom Components for "Technical Precision" Aesthetic ---

const SharpCard = ({ children, className = "" }) => (
  <div className={`bg-white border-none shadow-[0_2px_10px_-2px_rgba(0,0,0,0.1)] rounded-none ${className}`}>
    {children}
  </div>
);

const PrecisionInput = ({ className, ...props }) => (
  <Input
    className={`rounded-none border-0 border-b-2 border-stone-200 bg-stone-50/50 focus:ring-0 focus:border-[#1B4332] px-0 py-2 h-10 transition-all font-mono text-lg ${className}`}
    {...props}
  />
);

const DigitalDisplay = ({ label, value, unit, color = "text-stone-800", subtext }) => (
  <div className="flex flex-col p-3 bg-stone-900 text-stone-300 rounded-sm font-mono border-l-4 border-[#1B4332]">
    <span className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{label}</span>
    <div className={`text-2xl font-bold ${color} tabular-nums tracking-tight leading-none`}>
      {value} <span className="text-sm opacity-50 font-normal">{unit}</span>
    </div>
    {subtext && <span className="text-[10px] mt-1 opacity-50">{subtext}</span>}
  </div>
);

const RangeVisualizer = ({ label, value, limit, unit = "%", onChange, color = "bg-[#1B4332]" }) => {
  const numValue = parseFloat(value) || 0;
  const numLimit = parseFloat(limit) || 0;
  const percent = Math.min(100, Math.max(0, (numValue / (numLimit * 1.5 || 20)) * 100));
  const isOver = numLimit > 0 && numValue > numLimit;

  return (
    <div className="space-y-1">
      <div className="flex justify-between items-end mb-1">
        <Label className="text-xs uppercase tracking-wider text-stone-500">{label}</Label>
        <span className={`font-mono text-sm font-bold ${isOver ? 'text-red-600' : 'text-stone-700'}`}>
          {numValue}{unit} <span className="text-stone-300 font-normal">/ {numLimit}{unit}</span>
        </span>
      </div>
      <div className="h-1 bg-stone-100 w-full relative overflow-hidden">
        <div
          className={`h-full absolute left-0 top-0 transition-all duration-300 ${isOver ? 'bg-red-500 animate-pulse' : color}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

// --- Sub-components for Config ---

const BracketEditor = ({ brackets, onChange }) => {
  const addBracket = () => onChange([...brackets, { from: 0, to: 0, discount_pct: 0 }]);
  const removeBracket = (idx) => onChange(brackets.filter((_, i) => i !== idx));
  const updateBracket = (idx, field, val) => {
    const newBrackets = [...brackets];
    newBrackets[idx][field] = parseFloat(val) || 0;
    onChange(newBrackets);
  };

  return (
    <div className="space-y-2 bg-stone-50 p-3 border border-stone-200">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] uppercase font-bold text-stone-400">Tabela de Faixas</Label>
        <Button size="xs" variant="ghost" onClick={addBracket} className="h-6 text-[#1B4332]">
          <Plus className="w-3 h-3 mr-1" /> Add Faixa
        </Button>
      </div>
      {brackets.map((b, i) => (
        <div key={i} className="grid grid-cols-4 gap-2 items-center">
          <Input type="number" placeholder="De" value={b.from} onChange={e => updateBracket(i, 'from', e.target.value)} className="h-7 text-xs rounded-none" />
          <Input type="number" placeholder="Até" value={b.to} onChange={e => updateBracket(i, 'to', e.target.value)} className="h-7 text-xs rounded-none" />
          <Input type="number" placeholder="Desc %" value={b.discount_pct} onChange={e => updateBracket(i, 'discount_pct', e.target.value)} className="h-7 text-xs rounded-none" />
          <Button size="icon" variant="ghost" onClick={() => removeBracket(i)} className="h-7 w-7 text-red-400 hover:text-red-600">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>
  );
};

export default function MovimentacaoGraoForm({ initialData, armazens, estoques, safras, onSuccess, onCancel }) {
  const queryClient = useQueryClient();
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const [step, setStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [configProdutoAtivo, setConfigProdutoAtivo] = useState("soja");
  const [discountConfig, setDiscountConfig] = useState(buildDefaultDiscountConfig);
  const [aplicarDescontosAutomaticos, setAplicarDescontosAutomaticos] = useState(true);

  // State
  const defaultFormData = {
    tipo: "entrada",
    armazem_id: "",
    armazem_destino_id: "",
    produto: "soja",
    data: new Date().toISOString().split('T')[0],
    origem_destino: "",
    nota_fiscal: "",
    placa_veiculo: "",
    motorista: "",
    safra_id: "",
    talhao_id: "",
    pesagem_entrada: "",
    pesagem_saida: "",
    classificacao: "tipo_1",
    umidade: "",
    impureza: "",
    avariados: "",
    quebrados: "",
    ardidos: "",
    esverdeados: "",
    desconto_umidade_kg: "0",
    desconto_impureza_kg: "0",
    desconto_avariados_kg: "0",
    desconto_quebrados_kg: "0",
    desconto_ardidos_kg: "0",
    desconto_esverdeados_kg: "0",
    desconto_outros_kg: "0",
    preco_unitario: "",
    contrato_id: "",
    observacoes: ""
  };

  const [formData, setFormData] = useState(defaultFormData);

  // Queries
  const { data: propriedadeData } = useQuery({
    queryKey: ['propriedade_ticket', propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.Propriedade.get(propriedadeId) : Promise.resolve(null),
    enabled: !!propriedadeId
  });

  const { data: talhoes = [] } = useQuery({
    queryKey: ['talhoes', propriedadeId],
    queryFn: async () => {
      if (!propriedadeId) return [];
      const data = await agrogestao.entities.Talhao.filter({ propriedade_id: propriedadeId });
      return data || [];
    },
    enabled: !!propriedadeId
  });

  const { data: contratos = [] } = useQuery({
    queryKey: ['contratos_unificados', propriedadeId, formData.produto],
    queryFn: async () => {
      if (!propriedadeId) return [];

      // 1. Contratos da tabela real
      const reais = await agrogestao.entities.ContratoComercializacao.filter({
        propriedade_id: propriedadeId,
        produto: formData.produto,
        status: 'ativo'
      });

      // 2. Contratos "simulados" do financeiro
      const financeiro = await agrogestao.entities.ContaReceber.filter({
        propriedade_id: propriedadeId
      });

      const categoriasContrato = [
        'Venda de Grãos (Contrato)',
        'Contrato de Grãos',
        'Venda de Grãos'
      ];

      const simulados = financeiro
        .filter(c => categoriasContrato.includes(c.categoria) && (c.descricao || '').toLowerCase().includes(formData.produto.toLowerCase()))
        .map(c => ({
          id: c.id,
          cliente: c.cliente,
          produto: formData.produto,
          quantidade: 0, // Informação pode estar na observação
          unidade: 'un',
          isSimulado: true,
          descricao: c.descricao
        }));

      return [
        ...reais.map(r => ({ ...r, isSimulado: false })),
        ...simulados
      ];
    },
    enabled: !!propriedadeId && !!formData.produto
  });

  // Edit Mode Support
  useEffect(() => {
    if (initialData) {
      // Prioritize balanca weights, then peso_bruto (net before discounts), then legacy
      const pesagemEntrada = initialData.peso_balanca_entrada || initialData.peso_bruto || initialData.pesagem_entrada || 0;
      const pesagemSaida = initialData.peso_balanca_saida || 0;

      setFormData(prev => ({
        ...prev,
        ...initialData,
        // Map database columns to form fields
        pesagem_entrada: String(pesagemEntrada || ""),
        pesagem_saida: String(pesagemSaida || ""),
        talhao_id: String(initialData.talhao_id || ""),
        motorista: String(initialData.motorista || ""),
        umidade: String(initialData.umidade || ""),
        impureza: String(initialData.impureza || ""),
        avariados: String(initialData.avariados || ""),
        quebrados: String(initialData.quebrados || ""),
        ardidos: String(initialData.ardidos || ""),
        esverdeados: String(initialData.esverdeados || ""),
        desconto_umidade_kg: String(initialData.desconto_umidade || "0"),
        desconto_impureza_kg: String(initialData.desconto_impureza || "0"),
        desconto_avariados_kg: String(initialData.desconto_avariados || "0"),
        desconto_quebrados_kg: String(initialData.desconto_quebrados || "0"),
        desconto_ardidos_kg: String(initialData.desconto_ardidos || "0"),
        desconto_esverdeados_kg: String(initialData.desconto_esverdeados || "0"),
        preco_unitario: String((initialData.preco_unitario || 0) * 60),
        contrato_id: String(initialData.contrato_id || "")
      }));
    }
  }, [initialData]);

  const [saving, setSaving] = useState(false);
  const [gerarReceita, setGerarReceita] = useState(false);

  // Persistence
  const discountStorageKey = useMemo(() => (
    propriedadeId ? `graos_descontos_${propriedadeId}` : "graos_descontos"
  ), [propriedadeId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(discountStorageKey);
      if (raw) setDiscountConfig(mergeDiscountConfig(buildDefaultDiscountConfig(), raw ? JSON.parse(raw) : {}));
    } catch { }
  }, [discountStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(discountStorageKey, JSON.stringify(discountConfig));
    } catch { }
  }, [discountConfig, discountStorageKey]);

  // Helpers
  const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const num = Number(String(value).replace(",", "."));
    return Number.isFinite(num) ? num : 0;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const getConfigForProduto = (produto) => discountConfig[produto] || discountConfig.outro;

  const pesoBrutoReal = useMemo(() => Math.max(parseFloat(formData.pesagem_entrada) || 0, parseFloat(formData.pesagem_saida) || 0), [formData.pesagem_entrada, formData.pesagem_saida]);

  const configAtiva = useMemo(() => getConfigForProduto(formData.produto), [discountConfig, formData.produto]);

  const calculationResults = useMemo(() => {
    return calculateGrainDiscounts({
      grossWeight: toNumber(formData.pesagem_entrada),
      tareWeight: toNumber(formData.pesagem_saida),
      moistureMeasured: toNumber(formData.umidade),
      impurityMeasured: toNumber(formData.impureza),
      technicalShrinkPct: 0,
      policy: {
        standardMoisture: configAtiva.standardMoisture || 14.0,
        impurityTolerance: configAtiva.impurityTolerance || 0.0,
        factors: configAtiva.factors || []
      },
      defectMeasurements: {
        'AVARIADOS': toNumber(formData.avariados),
        'QUEBRADOS': toNumber(formData.quebrados),
        'ARDIDOS': toNumber(formData.ardidos),
        'ESVERDEADOS': toNumber(formData.esverdeados)
      }
    });
  }, [formData, configAtiva]);

  const pesoLiquidoFinal = calculationResults.finalWeight;
  const sacasFinais = pesoLiquidoFinal / 60;
  const descontoTotalKg = calculationResults.totalDiscountWeight;
  const precoSaca = toNumber(formData.preco_unitario);
  const valorBruto = sacasFinais * precoSaca;
  const valorLiquido = valorBruto; // Quality discounts are currently in weight (PESO mode in logic)

  const getClassificacaoLabel = (value) => classificacoesPadrao.find((c) => c.value === value)?.label || "Comercial";

  // Validation
  const validateStep = (currentStep) => {
    const errors = {};

    // Step 1 Validations
    if (currentStep === 1) {
      if (!formData.armazem_id) errors.armazem_id = "Selecione o armazém";
      if (!formData.produto) errors.produto = "Selecione o produto";
      if (!formData.data) errors.data = "Data é obrigatória";
      if (!formData.origem_destino) errors.origem_destino = "Informe origem/destino";

      const pesagemEntrada = toNumber(formData.pesagem_entrada);
      const pesagemSaida = toNumber(formData.pesagem_saida);

      if (pesagemEntrada <= 0) errors.pesagem_entrada = "Obrigatório e > 0";
      if (pesagemSaida <= 0) errors.pesagem_saida = "Obrigatório e > 0";
      if (calculationResults.grossWeight <= 0) errors.pesagem = "Pesagem inválida (líquido <= 0)";

      if (formData.tipo === 'entrada' || formData.tipo === 'transferencia') {
        if (!formData.safra_id) errors.safra_id = "Selecione a safra";
        if (formData.tipo === 'entrada' && !formData.talhao_id) errors.talhao_id = "Selecione o talhão";
      }
      if (formData.tipo === 'transferencia' && !formData.armazem_destino_id) errors.armazem_destino_id = "Selecione o armazém de destino";
    }

    // Step 2 Validations
    if (currentStep === 2) {
      if (!formData.classificacao) errors.classificacao = "Selecione a classificação";
      // Quality inputs are now handled by the engine, we just check for non-negative
      ['umidade', 'impureza', 'avariados', 'quebrados', 'ardidos', 'esverdeados'].forEach(field => {
        if (toNumber(formData[field]) < 0) errors[field] = "Não pode ser negativo";
      });
    }

    // Step 3 Validations
    if (currentStep === 3) {
      const precoUnitario = toNumber(formData.preco_unitario);
      if (precoUnitario <= 0) errors.preco_unitario = "Obrigatório e > 0";
    }

    return errors;
  };

  const handleNextStep = () => {
    const errors = validateStep(step);
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const allErrors = { ...validateStep(1), ...validateStep(2) };
      if (Object.keys(allErrors).length) throw new Error("Revise os campos obrigatórios");
      if (pesoLiquidoFinal <= 0) throw new Error("Peso líquido inválido");

      const precoSaca = toNumber(formData.preco_unitario);
      const resumoClassificacao = getClassificacaoLabel(formData.classificacao);

      const payload = {
        propriedade_id: propriedadeId,
        tipo: formData.tipo,
        armazem_id: formData.armazem_id,
        armazem_destino_id: formData.tipo === 'transferencia' ? formData.armazem_destino_id : null,
        produto: formData.produto,
        safra_id: formData.safra_id || null,
        data: formData.data,
        origem_destino: formData.origem_destino,
        nota_fiscal: formData.nota_fiscal,
        placa_veiculo: formData.placa_veiculo,
        peso_bruto: calculationResults.grossWeight,
        peso_liquido: pesoLiquidoFinal,
        quantidade: pesoLiquidoFinal,
        unidade: 'kg',
        umidade: parseFloat(formData.umidade) || null,
        impureza: parseFloat(formData.impureza) || null,
        avariados: parseFloat(formData.avariados) || null,
        quebrados: parseFloat(formData.quebrados) || null,
        ardidos: parseFloat(formData.ardidos) || null,
        esverdeados: parseFloat(formData.esverdeados) || null,
        desconto_umidade: (calculationResults.discounts['UMIDADE'] || 0),
        desconto_impureza: (calculationResults.discounts['IMPUREZA'] || 0),
        desconto_avariados: (calculationResults.qualityDiscounts['AVARIADOS']?.discount || 0),
        desconto_quebrados: (calculationResults.qualityDiscounts['QUEBRADOS']?.discount || 0),
        desconto_ardidos: (calculationResults.qualityDiscounts['ARDIDOS']?.discount || 0),
        desconto_esverdeados: (calculationResults.qualityDiscounts['ESVERDEADOS']?.discount || 0),
        talhao_id: formData.talhao_id || null,
        motorista: formData.motorista || "",
        peso_balanca_entrada: toNumber(formData.pesagem_entrada),
        peso_balanca_saida: toNumber(formData.pesagem_saida),
        preco_unitario: precoSaca / 60,
        valor_total: valorLiquido,
        contrato_id: formData.contrato_id || null,
        observacoes: formData.observacoes || ""
      };

      if (initialData?.id) {
        await agrogestao.entities.MovimentacaoGrao.update(initialData.id, payload);
      } else {
        await agrogestao.entities.MovimentacaoGrao.create(payload);
      }

      // Stock Updates
      const estoqueOrigem = estoques.find(e => e.armazem_id === formData.armazem_id && e.produto === formData.produto);
      if (formData.tipo === 'entrada') {
        const newQtd = (estoqueOrigem?.quantidade || 0) + pesoLiquidoFinal;
        if (estoqueOrigem) {
          await agrogestao.entities.EstoqueGrao.update(estoqueOrigem.id, {
            quantidade: newQtd,
            custo_medio: precoSaca / 60 // Atualiza para o novo preço unitário em KG
          });
        } else {
          await agrogestao.entities.EstoqueGrao.create({
            propriedade_id: propriedadeId,
            armazem_id: formData.armazem_id,
            produto: formData.produto,
            quantidade: newQtd,
            unidade: 'kg',
            custo_medio: precoSaca / 60,
            safra_id: formData.safra_id
          });
        }
      } else if (formData.tipo === 'saida') {
        if (estoqueOrigem) await agrogestao.entities.EstoqueGrao.update(estoqueOrigem.id, { quantidade: Math.max(0, estoqueOrigem.quantidade - pesoLiquidoFinal) });
        if (gerarReceita) {
          const obsReceita = formData.contrato_id ? `• contrato_id:${formData.contrato_id}` : "";
          await agrogestao.entities.ContaReceber.create({
            propriedade_id: propriedadeId,
            descricao: `Venda ${formData.produto} - NF ${formData.nota_fiscal}`,
            valor: valorLiquido,
            data_vencimento: formData.data,
            categoria: 'Venda de Grãos',
            cliente: formData.origem_destino,
            status: 'pendente',
            observacoes: obsReceita
          });
        }
      }

      // Invalidate queries to update Dashboard
      queryClient.invalidateQueries({ queryKey: ['movimentacoes_grao'] });
      queryClient.invalidateQueries({ queryKey: ['estoque_balanco'] });
      queryClient.invalidateQueries({ queryKey: ['contratos_comercializacao'] });
      queryClient.invalidateQueries({ queryKey: ['contratos_unificados'] });
      queryClient.invalidateQueries({ queryKey: ['contas_receber'] });

      // Count movements to generate ticket number
      let ticketNum = 1;
      try {
        const allMovs = await agrogestao.entities.MovimentacaoGrao.filter({ propriedade_id: propriedadeId });
        ticketNum = (allMovs || []).length;
      } catch { }

      const armazemSelecionado = armazens.find(a => a.id === formData.armazem_id);
      const safraSelecionada = safras.find(s => s.id === formData.safra_id);
      const talhaoSelecionado = talhoes.find(t => t.id === formData.talhao_id);

      // Print ticket
      imprimirTicketGrao({
        tipo: formData.tipo,
        produto: formData.produto,
        data: formData.data,
        origem_destino: formData.origem_destino,
        placa_veiculo: formData.placa_veiculo,
        motorista: formData.motorista,
        nota_fiscal: formData.nota_fiscal,
        classificacao: formData.classificacao,
        peso_balanca_entrada: toNumber(formData.pesagem_entrada),
        peso_balanca_saida: toNumber(formData.pesagem_saida),
        peso_bruto: calculationResults.grossWeight,
        peso_liquido: pesoLiquidoFinal,
        umidade: formData.umidade,
        impureza: formData.impureza,
        avariados: formData.avariados,
        quebrados: formData.quebrados,
        ardidos: formData.ardidos,
        esverdeados: formData.esverdeados,
        desconto_umidade: calculationResults.discounts['UMIDADE'] || 0,
        desconto_impureza: calculationResults.discounts['IMPUREZA'] || 0,
        desconto_avariados: (calculationResults.qualityDiscounts['AVARIADOS']?.discount || 0),
        desconto_quebrados: (calculationResults.qualityDiscounts['QUEBRADOS']?.discount || 0),
        desconto_ardidos: (calculationResults.qualityDiscounts['ARDIDOS']?.discount || 0),
        desconto_esverdeados: (calculationResults.qualityDiscounts['ESVERDEADOS']?.discount || 0),
        preco_unitario: precoSaca / 60,
        valor_total: valorLiquido,
        observacoes: formData.observacoes,
        propriedade: propriedadeData || { nome: '---' },
        armazem: armazemSelecionado || { nome: '---' },
        safra_nome: safraSelecionada?.nome || '---',
        talhao_nome: talhaoSelecionado?.nome || talhaoSelecionado?.descricao || '---',
        numero_ticket: ticketNum
      });

      toast.success("Movimentação registrada com sucesso!");
      onSuccess();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const produtoCurrent = produtos.find(p => p.value === formData.produto) || produtos[0];

  return (
    <div className="flex flex-col lg:flex-row h-full min-h-[calc(100vh-64px)] bg-stone-50/50 overflow-hidden">

      {/* LEFT: STATUS RAIL & MAIN FORM */}
      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto px-6 py-6 scrollbar-thin">

        {/* Header Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl font-bold text-stone-900 tracking-tight flex items-center gap-2">
              <span className="text-[#1B4332]">Movimentação</span>
              <span className="text-stone-300">/</span>
              <span>{step === 1 ? "Dados & Pesagem" : step === 2 ? "Classificação" : "Resumo Financeiro"}</span>
            </h2>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-1 w-8 rounded-full transition-all ${step >= s ? 'bg-[#1B4332]' : 'bg-stone-200'}`} />
              ))}
            </div>
          </div>
          <Badge variant="outline" className="rounded-none border-[#1B4332] text-[#1B4332] font-mono bg-white">
            {step}/3
          </Badge>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-8 animate-in slide-in-from-left-4 duration-500">
            {/* Type & Product Selector - Compact */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-stone-500">Operação</Label>
                <div className="flex rounded-none shadow-sm h-10">
                  <button onClick={() => setFormData({ ...formData, tipo: 'entrada' })} className={`flex-1 text-sm font-medium transition-colors ${formData.tipo === 'entrada' ? 'bg-[#1B4332] text-white' : 'bg-white text-stone-500 border border-stone-200'}`}>
                    Entrada
                  </button>
                  <button onClick={() => setFormData({ ...formData, tipo: 'saida' })} className={`flex-1 text-sm font-medium transition-colors ${formData.tipo === 'saida' ? 'bg-amber-600 text-white' : 'bg-white text-stone-500 border border-stone-200'}`}>
                    Saída
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-stone-500">Produto</Label>
                <Select value={formData.produto} onValueChange={(v) => setFormData({ ...formData, produto: v })}>
                  <SelectTrigger className="rounded-none border-stone-200 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {produtos.map(p => <SelectItem key={p.value} value={p.value}><span className="flex items-center gap-2">{p.icon} {p.label}</span></SelectItem>)}
                  </SelectContent>
                </Select>
                {formErrors.armazem_id && <p className="text-red-500 text-xs mt-1">{formErrors.armazem_id}</p>}
              </div>
            </div>

            {/* Main Info Blocks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Label className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200 pb-1 block">Logística</Label>
                <PartnerSearchPrecision
                  placeholder={formData.tipo === 'entrada' ? 'Nome do Fornecedor / Fazenda' : 'Nome do Cliente / Destino'}
                  value={formData.origem_destino}
                  tipo={formData.tipo === 'entrada' ? 'fornecedor' : 'cliente'}
                  onChange={val => setFormData({ ...formData, origem_destino: val })}
                />
                {formErrors.origem_destino && <p className="text-red-500 text-xs mt-1">{formErrors.origem_destino}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <PrecisionInput placeholder="Placa (ABC-1234)" value={formData.placa_veiculo} onChange={e => setFormData({ ...formData, placa_veiculo: e.target.value })} />
                  <PrecisionInput type="date" value={formData.data} onChange={e => setFormData({ ...formData, data: e.target.value })} />
                  {formErrors.data && <p className="text-red-500 text-xs mt-1">{formErrors.data}</p>}
                </div>
                <Select value={formData.armazem_id} onValueChange={(v) => setFormData({ ...formData, armazem_id: v })}>
                  <SelectTrigger className="rounded-none border-0 border-b-2 border-stone-200 bg-stone-50/50 px-0"><SelectValue placeholder="Selecione Armazém..." /></SelectTrigger>
                  <SelectContent>
                    {armazens.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <Label className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200 pb-1 block">Rastreabilidade</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Select value={formData.safra_id || '_none'} onValueChange={(v) => setFormData({ ...formData, safra_id: v === '_none' ? '' : v })}>
                    <SelectTrigger className="rounded-none border-0 border-b-2 border-stone-200 bg-stone-50/50 px-0"><SelectValue placeholder="Safra" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Sem Safra</SelectItem>
                      {safras.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <PrecisionInput placeholder="Nº Nota Fiscal" value={formData.nota_fiscal} onChange={e => setFormData({ ...formData, nota_fiscal: e.target.value })} />
                  {formErrors.nota_fiscal && <p className="text-red-500 text-xs mt-1">{formErrors.nota_fiscal}</p>}
                </div>

                {formData.tipo === 'saida' && (
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-stone-400">Vincular Contrato (Opcional)</Label>
                    <Select value={formData.contrato_id || '_none'} onValueChange={(v) => setFormData({ ...formData, contrato_id: v === '_none' ? '' : v })}>
                      <SelectTrigger className="rounded-none border-0 border-b-2 border-stone-200 bg-blue-50/50 px-0 h-10">
                        <SelectValue placeholder={contratos.length > 0 ? "Selecione um contrato..." : "Nenhum contrato disponível para este produto"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Nenhum contrato</SelectItem>
                        {contratos.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="flex items-center gap-2">
                              {c.isSimulado ? <Badge variant="outline" className="text-[8px] h-4">Finan</Badge> : <Badge className="text-[8px] h-4">Real</Badge>}
                              {c.isSimulado ? c.descricao : `#${c.id.substring(0, 4)} - ${c.cliente} (${c.quantidade} ${c.unidade})`}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.tipo === 'entrada' && (
                  <Select value={formData.talhao_id || '_none'} onValueChange={(v) => setFormData({ ...formData, talhao_id: v === '_none' ? '' : v })}>
                    <SelectTrigger className="rounded-none border-0 border-b-2 border-stone-200 bg-stone-50/50 px-0"><SelectValue placeholder="Talhão de Origem" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Nenhum</SelectItem>
                      {talhoes.map(t => <SelectItem key={t.id} value={t.id}>{t.nome || t.descricao}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Weighing Station - Digital Concept */}
            <div className="bg-stone-100 p-6 rounded-none space-y-4 shadow-inner">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-5 h-5 text-stone-600" />
                <h3 className="font-bold text-stone-700 uppercase tracking-wide text-sm">Estação de Pesagem</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Peso Entrada (Bruto)</Label>
                  <Input
                    type="number"
                    className="font-mono text-xl bg-white border-stone-300 rounded-none h-12"
                    placeholder="00000"
                    value={formData.pesagem_entrada}
                    onChange={e => setFormData({ ...formData, pesagem_entrada: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Peso Saída (Tara)</Label>
                  <Input
                    type="number"
                    className="font-mono text-xl bg-white border-stone-300 rounded-none h-12"
                    placeholder="00000"
                    value={formData.pesagem_saida}
                    onChange={e => setFormData({ ...formData, pesagem_saida: e.target.value })}
                  />
                </div>
                <DigitalDisplay
                  label="LÍQUIDO (CALC)"
                  value={calculationResults.grossWeight.toLocaleString('pt-BR')}
                  unit="KG"
                  color="text-emerald-500"
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleNextStep} className="bg-[#1B4332] text-white rounded-none px-8 h-12 font-bold uppercase tracking-wide hover:bg-[#2D6A4F] flex items-center gap-2">
                Próximo: Classificação <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

              {/* Classification Controls */}
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200 pb-1">Análise de Laboratório</Label>
                  <Button variant="ghost" size="sm" onClick={() => setDiscountDialogOpen(true)} className="text-stone-500 h-6 px-2 hover:text-[#1B4332]">
                    <Settings2 className="w-3 h-3 mr-1" /> Configurar Regras
                  </Button>
                </div>

                <div className="bg-white border border-stone-200 p-6 space-y-8 shadow-sm relative overflow-hidden">
                  {/* Visual indication of layered process */}
                  <div className="absolute top-0 left-0 w-1 h-full bg-[#1B4332] opacity-10"></div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <Label className="text-[10px] uppercase font-bold text-stone-400">Fase 1: Limpeza & Umidade</Label>
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <RangeVisualizer
                            label="Impureza (%)"
                            value={formData.impureza}
                            onChange={v => setFormData({ ...formData, impureza: v })}
                            limit={configAtiva.impurityTolerance}
                            color="bg-amber-500"
                          />
                          <PrecisionInput
                            type="number"
                            value={formData.impureza}
                            onChange={e => setFormData({ ...formData, impureza: e.target.value })}
                            className="text-sm h-8"
                          />
                        </div>
                        <div className="space-y-2">
                          <RangeVisualizer
                            label="Umidade (%)"
                            value={formData.umidade}
                            onChange={v => setFormData({ ...formData, umidade: v })}
                            limit={configAtiva.standardMoisture}
                            color="bg-blue-500"
                          />
                          <PrecisionInput
                            type="number"
                            value={formData.umidade}
                            onChange={e => setFormData({ ...formData, umidade: e.target.value })}
                            className="text-sm h-8"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <Label className="text-[10px] uppercase font-bold text-stone-400">Fase 2: Defeitos de Qualidade</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-500">Avariados (%)</Label>
                          <PrecisionInput
                            type="number"
                            value={formData.avariados}
                            onChange={e => setFormData({ ...formData, avariados: e.target.value })}
                            className="text-sm h-8"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-500">Ardidos (%)</Label>
                          <PrecisionInput
                            type="number"
                            value={formData.ardidos}
                            onChange={e => setFormData({ ...formData, ardidos: e.target.value })}
                            className="text-sm h-8"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-500">Quebrados (%)</Label>
                          <PrecisionInput
                            type="number"
                            value={formData.quebrados}
                            onChange={e => setFormData({ ...formData, quebrados: e.target.value })}
                            className="text-sm h-8"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-500">Esverdeados (%)</Label>
                          <PrecisionInput
                            type="number"
                            value={formData.esverdeados}
                            onChange={e => setFormData({ ...formData, esverdeados: e.target.value })}
                            className="text-sm h-8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label className="text-[10px] uppercase font-bold text-stone-400">Outros Descontos Manuais (KG)</Label>
                    <PrecisionInput
                      type="number"
                      value={formData.desconto_outros_kg}
                      onChange={e => setFormData({ ...formData, desconto_outros_kg: e.target.value })}
                      className="text-red-600"
                    />
                  </div>
                </div>
              </div>

              {/* Summary Stats Column */}
              <div className="space-y-4">
                <Label className="text-xs uppercase tracking-wider text-stone-500 block">Extrato de Descontos</Label>

                <div className="space-y-2">
                  {calculationResults.steps.map((step, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-white border border-stone-100 text-xs font-mono">
                      <span className="text-stone-400 uppercase">{step.name}</span>
                      <span className="text-red-500 font-bold">-{step.discount.toFixed(0)} kg</span>
                    </div>
                  ))}
                  {Object.entries(calculationResults.qualityDiscounts || {}).map(([code, data]) => (
                    data.discount > 0 && (
                      <div key={code} className="flex justify-between items-center p-2 bg-white border border-stone-100 text-xs font-mono">
                        <span className="text-stone-400 uppercase">{code}</span>
                        <span className="text-red-500 font-bold">-{data.discount.toFixed(0)} kg</span>
                      </div>
                    )
                  ))}
                </div>

                <DigitalDisplay
                  label="LÍQUIDO FINAL"
                  value={pesoLiquidoFinal.toFixed(0).replace(/\d(?=(\d{3})+$)/g, '$&.')}
                  unit="KG"
                  color="text-emerald-600"
                />
                <DigitalDisplay
                  label="SACAS (60KG)"
                  value={sacasFinais.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
                  unit="SC"
                  color="text-stone-800"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => setStep(1)} className="hover:bg-stone-100 rounded-none h-12">
                Voltar
              </Button>
              <Button onClick={handleNextStep} className="bg-[#1B4332] text-white rounded-none px-8 h-12 font-bold uppercase tracking-wide hover:bg-[#2D6A4F] flex items-center gap-2">
                Próximo: Fechamento <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <Label className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200 pb-1 block">Valores da Operação</Label>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-500">Preço / Saca (R$)</Label>
                      <PrecisionInput
                        type="number" step="0.01"
                        placeholder="0,00"
                        value={formData.preco_unitario}
                        onChange={e => setFormData({ ...formData, preco_unitario: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-500">Valor Total Estimado</Label>
                      <div className="h-10 flex items-center text-lg font-mono font-bold text-stone-900 border-b-2 border-transparent">
                        {valorLiquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </div>
                    </div>
                  </div>

                  {formData.tipo === 'saida' && (
                    <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-100 rounded-none">
                      <div className="space-y-0.5">
                        <Label className="font-bold text-amber-900">Gerar Conta a Receber</Label>
                        <p className="text-xs text-amber-700">Lança automaticamente no financeiro como "Pendente"</p>
                      </div>
                      <Switch checked={gerarReceita} onCheckedChange={setGerarReceita} />
                    </div>
                  )}

                  <div className="space-y-2 pt-4">
                    <Label className="text-xs uppercase tracking-wider text-stone-500">Observações</Label>
                    <Textarea
                      className="bg-white rounded-none border-stone-200 min-h-[100px]"
                      placeholder="Detalhes adicionais..."
                      value={formData.observacoes}
                      onChange={e => setFormData({ ...formData, observacoes: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Pre-flight Check */}
              <div className="bg-stone-100 p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider mb-4 border-b border-stone-200 pb-2">Resumo da Operação</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex justify-between">
                    <span className="text-stone-500">Movimento</span>
                    <span className="font-bold uppercase">{formData.tipo}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-stone-500">Produto</span>
                    <span className="font-bold flex items-center gap-2">{produtoCurrent.icon} {produtoCurrent.label}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-stone-500">Destino/Origem</span>
                    <span className="font-medium text-right max-w-[200px] truncate">{formData.origem_destino}</span>
                  </li>
                  <Separator className="bg-stone-200 my-2" />
                  <li className="flex justify-between">
                    <span className="text-stone-500">Peso Bruto Rec.</span>
                    <span className="font-mono">{pesoBrutoReal} kg</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-stone-500">Descontos</span>
                    <span className="font-mono text-red-600">-{descontoTotalKg} kg</span>
                  </li>
                  <li className="flex justify-between text-lg font-bold text-[#1B4332] pt-2">
                    <span>Líquido Final</span>
                    <span>{sacasFinais.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} sc</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => setStep(2)} className="hover:bg-stone-100 rounded-none h-12">
                Voltar
              </Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B4332] text-white rounded-none px-8 h-12 font-bold uppercase tracking-wide hover:bg-[#2D6A4F] flex items-center gap-2 shadow-lg shadow-emerald-900/20">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Confirmar & Salvar</>}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: RECEIPT PREVIEW (Always Visible on Desktop) */}
      <div className="hidden lg:flex w-80 bg-stone-100 border-l border-stone-200 flex-col p-6 shadow-[-10px_0_20px_-10px_rgba(0,0,0,0.05)] z-10">
        <div className="bg-white shadow-sm p-4 flex-1 flex flex-col font-mono text-xs relative">
          {/* Paper Tear Effect Top */}
          <div className="absolute top-0 left-0 w-full h-1 bg-[linear-gradient(45deg,transparent_33.333%,#fff_33.333%,#fff_66.667%,transparent_66.667%),linear-gradient(135deg,transparent_33.333%,#fff_33.333%,#fff_66.667%,transparent_66.667%)] bg-[length:10px_20px] -mt-2"></div>

          <div className="text-center mb-6 border-b-2 border-dashed border-stone-200 pb-4">
            <div className="font-bold text-lg uppercase tracking-widest text-stone-900 mb-1">TICKET</div>
            <div className="text-stone-400">{new Date().toLocaleDateString()}</div>
          </div>

          <div className="space-y-4 flex-1">
            <div className="flex justify-between">
              <span className="text-stone-400">TIPO</span>
              <span className="font-bold uppercase">{formData.tipo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-400">PRODUTO</span>
              <span className="font-bold uppercase">{formData.produto}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-400">PLACA</span>
              <span className="font-bold">{formData.placa_veiculo || '---'}</span>
            </div>

            <Separator className="border-dashed border-stone-200" />

            <div className="flex justify-between">
              <span className="text-stone-400">P. BRUTO</span>
              <span>{pesoBrutoReal}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-400">P. TARA</span>
              <span>{Math.min(parseFloat(formData.pesagem_entrada) || 0, parseFloat(formData.pesagem_saida) || 0)}</span>
            </div>

            <Separator className="border-dashed border-stone-200" />

            <div className="space-y-1 text-red-600">
              {calculationResults.steps.map((step, idx) => (
                step.discount > 0 && (
                  <div key={idx} className="flex justify-between">
                    <span>{step.name.toUpperCase()}</span>
                    <span>-{step.discount.toFixed(0)}</span>
                  </div>
                )
              ))}
              {Object.entries(calculationResults.qualityDiscounts || {}).map(([code, data]) => (
                data.discount > 0 && (
                  <div key={code} className="flex justify-between">
                    <span>{code}</span>
                    <span>-{data.discount.toFixed(0)}</span>
                  </div>
                )
              ))}
              {parseFloat(formData.desconto_outros_kg) > 0 && (
                <div className="flex justify-between">
                  <span>OUTROS</span>
                  <span>-{formData.desconto_outros_kg}</span>
                </div>
              )}
            </div>
          </div>

          <div className="border-t-2 border-stone-900 pt-4 mt-6">
            <div className="text-center">
              <span className="text-[10px] uppercase text-stone-400 tracking-widest">Peso Líquido Final</span>
              <div className="text-3xl font-bold text-[#1B4332] mt-1">{pesoLiquidoFinal}</div>
              <div className="text-xs font-bold text-stone-900">{sacasFinais.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} SC</div>
            </div>
          </div>

          {/* Barcode Mock */}
          <div className="mt-8 flex justify-center opacity-50">
            <div className="h-8 w-full bg-[repeating-linear-gradient(90deg,black,black_1px,transparent_1px,transparent_3px)]"></div>
          </div>
        </div>
      </div>

      {/* Config Dialog */}
      <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase tracking-wider text-sm">
              <Settings2 className="w-5 h-5" /> Configuração de Políticas por Produto
            </DialogTitle>
            <DialogDescription>
              Defina os padrões de umidade, impureza e descontos por faixas de qualidade.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="flex items-center gap-4 bg-stone-50 p-4 border border-stone-100">
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] uppercase font-bold text-stone-400">Produto Ativo</Label>
                <Select value={configProdutoAtivo} onValueChange={setConfigProdutoAtivo}>
                  <SelectTrigger className="rounded-none border-stone-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {produtos.map(p => <SelectItem key={p.value} value={p.value}>{p.icon} {p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-px h-10 bg-stone-200 mx-2" />
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] uppercase font-bold text-stone-400">Padrão Umidade (%)</Label>
                <Input type="number" step="0.1" value={getConfigForProduto(configProdutoAtivo).standardMoisture} onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  setDiscountConfig(prev => ({ ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], standardMoisture: val } }));
                }} className="h-10 rounded-none font-mono" />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] uppercase font-bold text-stone-400">Tolerância Impureza (%)</Label>
                <Input type="number" step="0.1" value={getConfigForProduto(configProdutoAtivo).impurityTolerance} onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  setDiscountConfig(prev => ({ ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], impurityTolerance: val } }));
                }} className="h-10 rounded-none font-mono" />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] uppercase font-bold text-stone-400">Quebra Técnica (%)</Label>
                <Input type="number" step="0.01" value={getConfigForProduto(configProdutoAtivo).technicalShrinkPct} onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  setDiscountConfig(prev => ({ ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], technicalShrinkPct: val } }));
                }} className="h-10 rounded-none font-mono" />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase font-bold flex items-center gap-2">
                  <TestTube className="w-4 h-4" /> Tabela de Descontos por Fator
                </Label>
                <Button size="sm" variant="outline" className="h-8 rounded-none text-[#1B4332]" onClick={() => {
                  setDiscountConfig(prev => {
                    const current = prev[configProdutoAtivo] || { factors: [] };
                    return { ...prev, [configProdutoAtivo]: { ...current, factors: [...(current.factors || []), { code: 'NOVO', tolerance: 0, discount_mode: 'PESO', brackets: [] }] } };
                  });
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Novo Fator
                </Button>
              </div>

              <div className="space-y-4">
                {(getConfigForProduto(configProdutoAtivo).factors || []).map((factor, fIdx) => (
                  <div key={fIdx} className="border border-stone-200 p-4 space-y-4 relative">
                    <Button size="icon" variant="ghost" className="absolute top-2 right-2 h-8 w-8 text-red-400" onClick={() => {
                      setDiscountConfig(prev => {
                        const factors = prev[configProdutoAtivo].factors.filter((_, i) => i !== fIdx);
                        return { ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], factors } };
                      });
                    }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-bold text-stone-400">Código</Label>
                        <Input value={factor.code} onChange={e => {
                          setDiscountConfig(prev => {
                            const factors = [...prev[configProdutoAtivo].factors];
                            factors[fIdx].code = e.target.value.toUpperCase();
                            return { ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], factors } };
                          });
                        }} className="h-8 rounded-none font-mono uppercase" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-bold text-stone-400">Tolerância (%)</Label>
                        <Input type="number" value={factor.tolerance} onChange={e => {
                          setDiscountConfig(prev => {
                            const factors = [...prev[configProdutoAtivo].factors];
                            factors[fIdx].tolerance = parseFloat(e.target.value) || 0;
                            return { ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], factors } };
                          });
                        }} className="h-8 rounded-none font-mono" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-bold text-stone-400">Modo</Label>
                        <Select value={factor.discount_mode} onValueChange={val => {
                          setDiscountConfig(prev => {
                            const factors = [...prev[configProdutoAtivo].factors];
                            factors[fIdx].discount_mode = val;
                            return { ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], factors } };
                          });
                        }}>
                          <SelectTrigger className="h-8 rounded-none"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PESO">Desconto em Peso (KG)</SelectItem>
                            <SelectItem value="PRECO">Desconto em Preço (R$)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <BracketEditor
                      brackets={factor.brackets || []}
                      onChange={brackets => {
                        setDiscountConfig(prev => {
                          const factors = [...prev[configProdutoAtivo].factors];
                          factors[fIdx].brackets = brackets;
                          return { ...prev, [configProdutoAtivo]: { ...prev[configProdutoAtivo], factors } };
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-stone-100">
              <Button variant="outline" onClick={() => setDiscountDialogOpen(false)} className="rounded-none h-12 px-8 uppercase font-bold tracking-widest text-xs">Cancelar</Button>
              <Button onClick={() => { setDiscountDialogOpen(false); toast.success("Política de descontos atualizada!"); }} className="bg-[#1B4332] text-white rounded-none h-12 px-8 uppercase font-bold tracking-widest text-xs">Salvar Alterações</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
